import Result from 'true-myth/result';

import { isParametersWithUrl } from '../http-fetcher/http-fetcher';

import type {
    HttpFetcher,
    HttpFetcherError,
    ParametersWithPathname,
    ParametersWithUrl,
    RequestHeaders,
} from '../http-fetcher/http-fetcher';
import type { Except } from 'type-fest';
import type { ZodPipe, ZodTypeAny } from 'zod';

export type ErrorReporter = {
    readonly reportError: (message: string, error: Error) => void;
};

type AnySchema = ZodPipe<ZodTypeAny> | ZodTypeAny;

type ValidationResult<SchemaType extends AnySchema> = Result<SchemaType['_output'], HttpFetcherError>;

type MethodExtendedWithValidation<T> = T extends (...params: infer U) => unknown
    ? <SchemaType extends AnySchema>(
          validationSchema: SchemaType,
          ...params: U
      ) => Promise<ValidationResult<SchemaType>>
    : never;

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface ValidatingHttpFetcher<T extends HttpFetcher = HttpFetcher> extends Except<
    HttpFetcher,
    'deleteJson' | 'get' | 'patchJson' | 'post' | 'postJson' | 'put' | 'putJson'
> {
    readonly deleteJson: MethodExtendedWithValidation<T['deleteJson']>;
    readonly get: MethodExtendedWithValidation<T['get']>;
    readonly patchJson: MethodExtendedWithValidation<T['patchJson']>;
    readonly post: MethodExtendedWithValidation<T['post']>;
    readonly postJson: MethodExtendedWithValidation<T['postJson']>;
    readonly put: MethodExtendedWithValidation<T['put']>;
    readonly putJson: MethodExtendedWithValidation<T['putJson']>;
}

type ValidatingHttpFetcherDependencies<T> = {
    readonly errorReporter: ErrorReporter;
    readonly httpFetcher: T;
};

const reportSchemaError = (
    errorReporter: ErrorReporter,
    method: string,
    parameters: ParametersWithPathname | ParametersWithUrl,
    error: Error,
): void => {
    try {
        const path = isParametersWithUrl(parameters) ? parameters.url.toString() : parameters.pathname.toString();

        errorReporter.reportError(`Schema validation failed for ${method} ${path}`, error);
    } catch (_) {}
};

export const createValidatingHttpFetcher = <T extends HttpFetcher>({
    errorReporter,
    httpFetcher,
}: ValidatingHttpFetcherDependencies<T>): ValidatingHttpFetcher => {
    const validateResult = (
        parameters: ParametersWithPathname | ParametersWithUrl,
        method: string,
        validationSchema: AnySchema,
        result: Result<unknown, Error>,
    ): ValidationResult<AnySchema> => {
        if (result.isOk) {
            const validationResult = validationSchema.safeParse(result.value);

            if (validationResult.success === true) {
                return Result.ok(validationResult.data);
            }

            reportSchemaError(errorReporter, method, parameters, validationResult.error);

            return Result.err(validationResult.error);
        }

        return result;
    };

    return {
        addHttpErrorHandler: httpFetcher.addHttpErrorHandler,
        delete: httpFetcher.delete,
        async deleteJson(validationSchema, parameters) {
            const result = await httpFetcher.deleteJson(parameters);

            return validateResult(parameters, 'deleteJson', validationSchema, result);
        },
        async get(validationSchema, parameters) {
            const result = await httpFetcher.get(parameters);

            return validateResult(parameters, 'get', validationSchema, result);
        },
        async patchJson(validationSchema, parameters) {
            const result = await httpFetcher.patchJson(parameters);

            return validateResult(parameters, 'patchJson', validationSchema, result);
        },
        async post(validationSchema, parameters) {
            const result = await httpFetcher.post(parameters);

            return validateResult(parameters, 'post', validationSchema, result);
        },
        async postJson(validationSchema, parameters) {
            const result = await httpFetcher.postJson(parameters);

            return validateResult(parameters, 'postJson', validationSchema, result);
        },
        async put(validationSchema, parameters) {
            const result = await httpFetcher.put(parameters);

            return validateResult(parameters, 'put', validationSchema, result);
        },
        async putJson(validationSchema, parameters) {
            const result = await httpFetcher.putJson(parameters);

            return validateResult(parameters, 'putJson', validationSchema, result);
        },
        set requestHeaders(_requestHeaders: RequestHeaders) {
            httpFetcher.requestHeaders = _requestHeaders;
        },
    };
};
